import {useState, useEffect, useRef} from 'react'
import classNames from 'classnames'
import {CircularProgress, Box, Typography} from '@mui/material'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Checkbox from '@mui/material/Checkbox'
import ListItemText from '@mui/material/ListItemText'
import {useSelector} from 'react-redux'
import {
  classes,
  StyledSelect,
  SelectInput,
  StyledMenuItem,
  StyledTypography,
  StyledBannerBox,
  StyledBannerContent,
  StyledBannerIcon,
  StyledBannerClose,
  StyledBannerTextBox
} from './StateFilterStyles'
import {useUpdateDefaultContext} from '../../../store/hooks/breadCrumbs'
import {selectFilters, selectFilterDetailsLoading} from '../../../store/slices/breadCrumbsSlice'
import {selectStateListFilter} from '../../../store/slices/filterListDetailSlice'
import {useFilterManagement} from '../../../store/hooks/useFilterManagement'
import loadingTitles from '../../../appConfig/loadingTitles.config.json'
import PropTypes from 'prop-types'
import {useConfig} from '../../../store/hooks/config'
import {AnalyticsHandler} from '../../../shared/handlers/analytics/handlers'

const SelectArrow = ({className, ...rest}) => {
  return <ExpandMore aria-label='dropdown selection' {...rest} className={classNames(className, classes.icon)} />
}

SelectArrow.propTypes = {
  className: PropTypes.string
}

const StateFilterComponent = ({isLoading}) => {
  const [showSelect, setShowSelect] = useState(false)
  const filters = useSelector(selectFilters)
  const filterDetailsLoading = useSelector(selectFilterDetailsLoading)
  const stateListFilter = useSelector(selectStateListFilter)
  const {updateDefaultState} = useUpdateDefaultContext()
  const {removedAndResetFilters, clearFiltersRemovedAndReset} = useFilterManagement()
  const {statesConfig} = useConfig() || {}
  let selectedValue = null
  const getStateAbbreviation = (selectedState) => {
    const getSelectedStatesData = selectedState.map((sateName) => {
      return getAllStates.find((obj) => obj.STATE_NAME === sateName)
    })
    return getSelectedStatesData.map((item) => item.STATE_ABBREVIATION)
  }

  const prevStateRef = useRef()

  const updateToEntireUnitedState = (getAllStates) => {
    if (getAllStates[0].STATE_NAME === statesConfig.entireUnitedStateLabel) {
      updateDefaultState([getAllStates[0].STATE_NAME], getAllStates, getStateAbbreviation([]))
      // Filter removal/reset is now handled automatically by useFilterBusinessLogic
      // via updateFiltersOnStateChange thunk when state changes
    }
  }

  const getStateSelected = (selectedState) => {
    const index = selectedState?.indexOf(statesConfig.entireUnitedStateLabel)
    const multipleSelection = index > 0 ? [selectedState[index]] : selectedState
    selectedState = index === 0 ? selectedState.splice(1, 1) : multipleSelection
    return selectedState
  }
  const updateMultiState = (selectedState, getAllStates) => {
    selectedState = getStateSelected(selectedState)
    updateDefaultState(selectedState, getAllStates, getStateAbbreviation(selectedState))
    // Filter removal/reset is now handled automatically by useFilterBusinessLogic
    // via updateFiltersOnStateChange thunk when state changes
  }

  const filtersCopy = structuredClone(filters)
  const getAllStates = filtersCopy.find((value) => value.key === 'state')?.values
  const handleDropdownSelectorClick = (event) => {
    if (event.target.value.length > 0) {
      const selectedState = event.target.value
      updateMultiState(selectedState, getAllStates)
    } else {
      updateToEntireUnitedState(getAllStates)
    }
  }

  const showNoSelectedText = (selected, filter) => {
    if (!selected && selected.length === 0) {
      return <em>Select a {filter.label?.slice(0, -1)}</em>
    }
  }

  const stateSelectedDefaultValue = (filter) => {
    return filter.default ? filter.default : null
  }

  // Legacy removeFilter function removed - filter removal/reset is now handled
  // automatically by updateFiltersOnStateChange thunk in useFilterBusinessLogic

  const renderMenuItem = (option, index, selectedValue) => {
    return (
      <StyledMenuItem
        sx={{
          py: 0,
          px: '8px'
        }}
        key={`${option.STATE_NAME}_${index}`}
        selected={true}
        value={option.STATE_NAME}
        disabled={option.STATE_NAME && !option.IS_ACTIVE}
      >
        <>
          <Checkbox checked={selectedValue?.indexOf(option.STATE_NAME) > -1} />
          <ListItemText
            variant='subtitle1'
            disableTypography
            sx={{
              color: (theme) => theme.palette.hpBlack
            }}
            primary={option.STATE_NAME}
          />
        </>
      </StyledMenuItem>
    )
  }
  const [stateFilter, setStateFilter] = useState(filters[0])
  const hasInitialized = useRef(false)

  useEffect(() => {
    const tempstateFilter = filters[0]
    if (
      tempstateFilter &&
      tempstateFilter?.values?.length > 0 &&
      !filterDetailsLoading &&
      stateListFilter?.length > 0
    ) {
      const stateFilterID = stateListFilter?.map(({STATE_ID}) => STATE_ID)
      const selectedValue = tempstateFilter.values.filter((value) =>
        value.STATE_ID === '0' ? true : stateFilterID.includes(value.STATE_ID)
      )
      const condition = {
        ...tempstateFilter,
        values: selectedValue
      }

      const newStateName = selectedValue[0]?.STATE_NAME
      const prevStateName = stateFilter?.values?.[0]?.STATE_NAME

      // Only update if this is the first initialization and state name actually changed
      if (!hasInitialized.current && newStateName !== prevStateName) {
        hasInitialized.current = true
        updateDefaultState([newStateName], getAllStates, getStateAbbreviation([newStateName]))
      }

      setStateFilter(condition)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDetailsLoading, stateListFilter, filters])

  useEffect(() => {
    if (stateFilter) {
      const currentState = stateSelectedDefaultValue(stateFilter) || []
      const lastSelectedState = currentState.at(-1)
      const prevState = JSON.parse(sessionStorage.getItem('stateFilter')) || []
      if (!prevState?.includes(lastSelectedState)) {
        AnalyticsHandler.onStateApply({name: lastSelectedState})
      }
      sessionStorage.setItem('stateFilter', JSON.stringify(currentState))
    }
  }, [stateFilter])

  const [removedFilters, setRemovedFilters] = useState([])
  const [resetFilters, setResetFilters] = useState([])

  // useEffect to handle filtersRemoved and filtersReset
  useEffect(() => {
    // display a banner notice that filters were removed and reset
    const filtersRemoved = removedAndResetFilters?.removed
    const filtersReset = removedAndResetFilters?.reset
    if (prevStateRef.current !== stateFilter) {
      prevStateRef.current = stateFilter
      if (filtersRemoved?.length > 0 || filtersReset?.length > 0) {
        setRemovedFilters(filtersRemoved)
        setResetFilters(filtersReset)
        clearFiltersRemovedAndReset()
        setTimeout(() => {
          setRemovedFilters([])
          setResetFilters([])
        }, 10000)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateFilter])

  const renderBannerMessage = () => {
    return (
      (removedFilters.length > 0 || resetFilters.length > 0) && (
        <StyledBannerBox role='alert' aria-live='polite'>
          <StyledBannerContent>
            <StyledBannerIcon />
            <StyledBannerTextBox>
              <Typography>Filters have been updated based on your geography selection.</Typography>
              {resetFilters.length > 0 && (
                <Typography>
                  <b>Reset Filters: </b>
                  {resetFilters.join(', ')}{' '}
                </Typography>
              )}
              {removedFilters.length > 0 && (
                <Typography>
                  <b>Removed Filters: </b>
                  {removedFilters.join(', ')}{' '}
                </Typography>
              )}
            </StyledBannerTextBox>
            <StyledBannerClose
              onClick={() => {
                setRemovedFilters([])
                setResetFilters([])
              }}
              aria-label='Close notification'
            />
          </StyledBannerContent>
        </StyledBannerBox>
      )
    )
  }

  const renderStateFilters = () => {
    selectedValue = stateSelectedDefaultValue(stateFilter)
    const filterLabel = stateFilter?.label?.replaceAll(/\(s\):$/g, 's')?.replaceAll(':', '')
    const ariaLabel = `Select ${filterLabel}, ${selectedValue}`
    return (
      <>
        {filterDetailsLoading && <CircularProgress size={20} title={loadingTitles.stateBreadcrumb} />}
        {!filterDetailsLoading &&
          (stateFilter?.values?.length < 2 ? (
            <StyledTypography tabIndex={0} aria-label={ariaLabel}>
              {selectedValue}
            </StyledTypography>
          ) : (
            <StyledSelect
              open={showSelect}
              onOpen={() => setShowSelect(true)}
              onClose={() => setShowSelect(false)}
              size='small'
              fullWidth
              MenuProps={{
                MenuListProps: {
                  sx: {
                    py: '0px'
                  }
                },
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left'
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left'
                },
                PaperProps: {
                  sx: {
                    scrollPaddingTop: '0',
                    maxHeight: {xs: '50%', md: '60%', xl: '75%'},
                    maxWidth: 'fit-content',
                    marginTop: (theme) => theme.spacing(1),
                    boxShadow: 2
                  }
                },
                autoFocus: true
              }}
              labelId={stateFilter.key}
              id={stateFilter.key}
              value={selectedValue || []}
              onChange={(event) => handleDropdownSelectorClick(event)}
              input={<SelectInput inputProps={{'aria-label': 'Select a state'}} />}
              multiple
              IconComponent={SelectArrow}
              renderValue={(selected) => {
                showNoSelectedText(selected, stateFilter)
                return Array.isArray(selected) ? (
                  <span className={classes.gutterRight}>{selected.join(', ')}</span>
                ) : (
                  <span className={classes.gutterRight}>{selected.toLowerCase()}</span>
                )
              }}
            >
              {stateFilter?.values?.map((option, index) => {
                return renderMenuItem(option, index, selectedValue)
              })}
            </StyledSelect>
          ))}
      </>
    )
  }

  return (
    <Box>
      {isLoading && stateFilter && renderStateFilters()}
      {renderBannerMessage()}
    </Box>
  )
}
StateFilterComponent.propTypes = {
  isLoading: PropTypes.bool
}
export default StateFilterComponent

/* Function Descriptions:
  - SelectArrow: Renders a dropdown arrow icon for the select component.
  - getStateAbbreviation: Maps state names to their abbreviations.
  - updateToEntireUnitedState: Updates the state filter to "Entire United States".
  - getStateSelected: Handles selection logic for "Entire United States" and multiple states.
  - updateMultiState: Updates the state filter for multiple selected states.
  - handleDropdownSelectorClick: Handles dropdown selection changes and updates state filters.
  - showNoSelectedText: Displays placeholder text when no state is selected.
  - stateSelectedDefaultValue: Retrieves the default selected state value.
  - renderMenuItem: Renders a menu item for each state in the dropdown.
  - renderStateFilters: Renders the state filter dropdown or selected state text.
  - renderBannerMessage: Renders the notification banner for removed/reset filters.
  - StateFilterComponent: Renders the state filter dropdown component with notification banner.
  
  Note: Filter removal/reset on state change is now handled by updateFiltersOnStateChange
  thunk in useFilterBusinessLogic. This component only displays the notification banner.
*/
